import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/** Save a PDF the API hands back as a blob, under a name the browser keeps. */
export const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/** The filename the API chose (Content-Disposition), so the saved file matches what's inside it. */
export const nameFrom = (r, fallback) => {
  const h = r?.headers?.["content-disposition"] || "";
  const star = h.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) { try { return decodeURIComponent(star[1]); } catch { /* fall through */ } }
  const plain = h.match(/filename="([^"]+)"/i);
  return plain ? plain[1] : fallback;
};

export const packName = (company, ref) => `Connexis Deal Pack - ${company || "Company"}${ref ? ` (${ref})` : ""}.pdf`;

/** The Hub's view of a case's pitch — polls while Connie is drafting. */
export function useHubPitch(leadId) {
  const [data, setData] = useState(null);
  const timer = useRef(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hub/leads/${leadId}/pitch`);
      setData(r.data);
      clearTimeout(timer.current);
      if (r.data.status === "drafting") timer.current = setTimeout(load, 4000);
    } catch { setData({ status: "none", available: [], figures: [] }); }
  }, [leadId]);
  useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);

  const draft = async () => {
    const r = await api.post(`/hub/leads/${leadId}/pitch/draft`);
    setData(r.data);
    clearTimeout(timer.current);
    timer.current = setTimeout(load, 4000);
  };
  const save = async (patch) => { const r = await api.patch(`/hub/leads/${leadId}/pitch`, patch); setData(r.data); return r.data; };
  const approve = async (on) => { const r = await api.post(`/hub/leads/${leadId}/pitch/approve`, { on }); setData(r.data); };
  const preview = async (company, ref) => {
    const r = await api.get(`/hub/leads/${leadId}/deal-pack.pdf`, { responseType: "blob" });
    saveBlob(r.data, nameFrom(r, packName(company, ref)));
  };
  return { data, load, draft, save, approve, preview };
}

/** A lender desk's view: the approved pitch (or nothing), the pack and the send button. */
export function useLenderPitch(leadId) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/lender/leads/${leadId}/pitch`).then((r) => setData(r.data)).catch(() => setData({ status: "none" }));
  }, [leadId]);
  useEffect(() => { load(); }, [load]);
  const pack = async (company, ref) => {
    const r = await api.get(`/lender/leads/${leadId}/deal-pack.pdf`, { responseType: "blob" });
    saveBlob(r.data, nameFrom(r, packName(company, ref)));
  };
  return { data, load, pack };
}
